/**
 * ProjectRepository wraps the backend project endpoints.
 *
 * Both calls return only {id, name}: the timesheet screen uses them to fill
 * dropdowns, nothing else reads the rest of the payload yet.
 */
import { post } from '../../../shared/api/post';
import { PROJECT_ENDPOINTS } from '../../../shared/constants';

const relId = (value) => {
  if (Array.isArray(value)) return value[0] ?? null;
  if (value && typeof value === 'object') return value.id ?? null;
  return value ?? null;
};

const relName = (value) => {
  if (Array.isArray(value)) return value[1] ?? null;
  if (value && typeof value === 'object') return value.name ?? null;
  return null;
};

const options = (data) =>
  (Array.isArray(data.items) ? data.items : []).map((row) => {
    const project = row.project ?? row.project_id;
    return {
      id: row.id,
      name: row.name,
      projectId: relId(project),
      projectName: row.project_name ?? relName(project),
    };
  });

export const ProjectRepository = {
  /**
   * Fetch the projects the caller is involved in.
   *
   * @param {{accessToken?:string|null}} params
   * @returns {Promise<Array<{id:number,name:string}>>}
   * @throws {Error} When the backend response is not successful.
   */
  listProjects: async ({ accessToken } = {}) =>
    options(await post(PROJECT_ENDPOINTS.listProjects, {}, accessToken)),

  /**
   * Fetch the caller's tasks. When projectId is omitted, the backend returns
   * all tasks visible to the caller.
   *
   * @param {{projectId:number|string,accessToken?:string|null}} params
   * @returns {Promise<Array<{id:number,name:string}>>}
   * @throws {Error} When the backend response is not successful.
   */
  listTasks: async ({ projectId, accessToken } = {}) =>
    options(
      await post(
        PROJECT_ENDPOINTS.listTasks,
        projectId ? { project_id: Number(projectId) } : {},
        accessToken,
      ),
    ),
};
